/* eslint-disable @typescript-eslint/indent */
import useMasterclassCountdownTimer from '@/hooks/useMasterclassCountdownTimer';
import useScreenWidth from '@/hooks/useScreenWidth';
import { getPriceDetails, renderCurrencySymbol } from '@/utils/helper/helperFunction';
import { Tooltip, Typography } from '@pwskills/rachnaui';
import { classNames } from '@pwskills/rachnaui/utils';
import useMasterclassDescriptionStore from '@/store/MasterClassDescription/useMasterclasDescriptionStore';
import { IMasterclassDescription } from '@/interfaces/masterclassDescription';
import {
  MASTERCLASS_REGISTRATION_AND_CLASS_STATUS,
  getRegistrationStatus,
} from '@/utils/common/masterclassDescription';
import { useRouter } from 'next/router';
import RegistrationFlow from '../PaymentFlow';

const StickyNavbar = ({ isFixed }: { isFixed?: boolean }) => {
  const router = useRouter();
  const isDashBoard = router?.pathname?.includes('dashboard');
  const masterclassDetails = useMasterclassDescriptionStore(
    (state: { masterclassDetails: IMasterclassDescription }) => state.masterclassDetails
  );
  const { pricing, masterclassTimings, startDate, endDate } = masterclassDetails || {};

  const setIsMasterclassBought = useMasterclassDescriptionStore(
    (state: { setIsMasterclassBought: (data: boolean) => void }) => state.setIsMasterclassBought
  );
  const isMasterclassBought = useMasterclassDescriptionStore(
    (state: { isMasterclassBought: boolean }) => state.isMasterclassBought
  );

  const priceDetails = getPriceDetails(pricing) || {
    actualPrice: 0,
    currentPrice: 0,
    discount: 0,
    isCourseFree: true,
  };

  const { actualPrice, currentPrice, discount, isCourseFree } = priceDetails || {};
  const { startTime, endTime } = masterclassTimings || {};
  const actualStartTime = startDate || startTime;
  const actualEndTime = endDate || endTime;

  const registrationStatus = getRegistrationStatus({
    startDate: actualStartTime as string,
    endDate: actualEndTime as string,
  });

  const { getDay, gethours, getMinutes, getSeconds } =
    useMasterclassCountdownTimer(actualStartTime);

  // to calculate countdown timer in strip
  const days = getDay();
  const hours = gethours();
  const minutes = getMinutes();
  const seconds = getSeconds();

  const screenWidth = useScreenWidth();

  const getHeader = (headerText: string) => {
    return (
      <Typography
        variant="semi-bold"
        component="body-small"
        className=" text-gray-650 !text-[14px] !leading-[22px] medium:!text-[12px] medium:!leading-[18px] large:!text-[14px] large:!leading-[22px]"
      >
        {headerText}
      </Typography>
    );
  };

  const getSubText = (
    subText: string,
    className?: string,
    variant?: 'bold' | 'semi-bold' | 'light' | 'medium' | 'underline' | 'strikethrough' | 'regular'
  ) => {
    return (
      <Typography
        variant={variant ?? 'bold'}
        component="subheading"
        className={classNames(
          'text-gray-850 !text-[18px] !leading-[26px] medium:!text-[16px] medium:!leading-[24px] large:!text-[18px] large:!leading-[26px]',
          className ?? ''
        )}
      >
        {subText}
      </Typography>
    );
  };

  const getDurationText = (text: string) => {
    return (
      <Typography
        variant="semi-bold"
        component="label"
        className=" text-gray-650 !text-[10px] !leading-4"
      >
        {text}
      </Typography>
    );
  };

  const getDateInFormat = () => {
    const date = new Date(actualStartTime as string);
    if (screenWidth && screenWidth > 640) {
      const daysOfWeek = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      const months = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];

      const utcDayOfWeek = daysOfWeek[date.getDay()];
      const utcDay = String(date.getDate()).padStart(2, '0'); // Add leading zero if necessary
      const utcMonth = months[date.getMonth()];
      const utcYear = date.getFullYear();

      return `${utcDayOfWeek}, ${utcDay} ${utcMonth}, ${utcYear}`;
    }
    const utcDay = String(date.getDate()).padStart(2, '0'); // Add leading zero if necessary
    const utcMonth = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed, so add 1
    const utcYear = date.getFullYear();

    return `${utcDay}/${utcMonth}/${utcYear}`;
  };

  const getTimeFromDate = (timestamp: string) => {
    const date = new Date(timestamp);

    // Extract hours and minutes
    let hoursInLocal = date.getHours();
    const minutesInLocal = date.getMinutes();

    // Determine AM/PM
    const ampm = hoursInLocal >= 12 ? 'PM' : 'AM';

    // Convert hours from 24-hour to 12-hour format
    hoursInLocal %= 12;
    hoursInLocal = hoursInLocal || 12; // Hour '0' should be '12'

    // Format minutes to always show two digits
    const formattedMinutes = minutesInLocal < 10 ? `0${minutesInLocal}` : minutesInLocal;

    // Combine formatted hours, minutes, and AM/PM
    return `${hoursInLocal}:${formattedMinutes} ${ampm}`;
  };

  const getMasterClassDuration = () => {
    return `${getTimeFromDate(actualStartTime as string)} to ${getTimeFromDate(
      actualEndTime as string
    )} `;
  };

  const isClassEnded = () => {
    return registrationStatus === MASTERCLASS_REGISTRATION_AND_CLASS_STATUS.CLASS_ENDED;
  };

  const isClassStarted = () => {
    return registrationStatus === MASTERCLASS_REGISTRATION_AND_CLASS_STATUS.CLASS_STARTED;
  };
  return (
    <div
      className={`z-[9999999] ${
        isDashBoard
          ? 'flex flex-col w-full bg-white'
          : !isFixed
          ? `w-full medium:!py-0 flex flex-col medium:!px-[0px] mx-auto ${
              isClassEnded()
                ? 'large:!w-[45%]'
                : isMasterclassBought
                ? 'large:!w-[70%]'
                : isClassStarted()
                ? 'large:!w-[75%]'
                : 'large:!w-[85%]'
            }`
          : 'z-[9999999]  !px-0 !py-0 w-[100%] mx-auto'
      }`}
      style={{
        transition: 'width 0.5s ease', // Add transition for padding changes
      }}
    >
      <div
        className={
          isDashBoard
            ? 'flex justify-center large:!justify-end  p-4 large:!w-[calc(100vw-256px)] relative large:!left-[256px]'
            : 'medium:px-[18px] medium:py-6 large:px-14 large:py-6 bg-white rounded-t-xl medium:shadow-medium'
        }
      >
        <div
          className={
            isDashBoard
              ? 'flex justify-between w-full'
              : 'flex flex-col medium:flex-row  medium:items-center medium:justify-between large:justify-end gap-x-4 large:!gap-10'
          }
        >
          <div
            className={
              isDashBoard
                ? 'flex flex-wrap  items-center justify-between w-full'
                : 'flex flex-wrap medium:!flex-nowrap  medium:items-center gap-4 medium:!gap-[24px] large:!gap-[60px] p-4 medium:!p-0 medium:w-[70%] medium:!justify-center large:!justify-center flex-1'
            }
          >
            {!isClassEnded() && (
              <div className="flex flex-col gap-1 w-[calc((100%-16px)/2)] medium:w-auto ">
                {getHeader('Starts in')}
                <div className="flex gap-2 items-center">
                  {days && (
                    <div className="flex flex-col gap-1">
                      {getSubText(days)}
                      {getDurationText(days > '1 ' ? 'Days' : 'Day')}
                    </div>
                  )}

                  {hours && (
                    <div className="flex flex-col gap-1">
                      {getSubText(hours)}
                      {getDurationText(hours > '1' ? 'Hours' : 'Hour')}
                    </div>
                  )}
                  {minutes && (
                    <div className="flex flex-col gap-1">
                      {getSubText(minutes)}
                      {getDurationText(minutes > '1' ? 'Minutes' : 'Minute')}
                    </div>
                  )}
                  {days && days === '0' && (
                    <div className="flex flex-col gap-1">
                      {getSubText(seconds)}
                      {getDurationText('Seconds')}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-1  w-[calc((100%-16px)/2)] medium:w-auto">
              {getHeader('Date')}

              {getSubText(getDateInFormat())}
            </div>
            <div className="flex flex-col gap-1  w-[calc((100%-16px)/2)] medium:w-auto">
              {getHeader('Time')}
              {getSubText(getMasterClassDuration())}
            </div>
            {!isMasterclassBought &&
              registrationStatus === MASTERCLASS_REGISTRATION_AND_CLASS_STATUS.OPEN && (
                <div className="flex flex-col gap-1 w-[calc((100%-16px)/2)] medium:w-auto">
                  {getHeader('Price')}
                  {isCourseFree ? (
                    getSubText('Free')
                  ) : (
                    <div className="flex gap-[2px]  items-center">
                      <div className="flex gap-[2px] justify-center items-center">
                        {getSubText(renderCurrencySymbol())}

                        {getSubText(
                          Math.ceil(currentPrice as number) as unknown as string,
                          '!text-[18px] !leading-[26px]'
                        )}
                      </div>
                      {/* strike out when offer is greater than 0 */}
                      {discount
                        ? discount > 0 &&
                          getSubText(
                            Math.ceil(actualPrice as number) as unknown as string,
                            '!text-gray-650 !text-[14px] !leading-[22px] !font-normal',
                            'strikethrough'
                          )
                        : ''}
                    </div>
                  )}
                </div>
              )}
            {!isClassEnded() && (
              <div
                id="testingNav"
                className="fixed !p-4 bottom-0 left-0 medium:relative medium:bottom-auto !w-full medium:!w-fit bg-blue-50 medium:bg-white justify-center medium:!py-0 medium:!px-0 button-container"
              >
                <Tooltip
                  text={registrationStatus}
                  className={`${
                    registrationStatus !== MASTERCLASS_REGISTRATION_AND_CLASS_STATUS.OPEN &&
                    !isMasterclassBought
                      ? ''
                      : 'hidden'
                  }`}
                >
                  <RegistrationFlow
                    masterclassId={
                      (masterclassDetails?.masterclassId as string) ||
                      (masterclassDetails?.courseId as string)
                    }
                    masterclassSlug={masterclassDetails?.slug as string}
                    masterclassName={masterclassDetails?.slug as string}
                    isUserRegistered={isMasterclassBought}
                    isFixed={isFixed}
                    registrationStatus={registrationStatus}
                    pricing={priceDetails}
                    setUserRegistered={setIsMasterclassBought}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyNavbar;
